import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import configService from '../services/configService';
import voiceStreamingService from '../services/voiceStreamingService';
import PersonalityTraits from './PersonalityTraits';

const VoiceSelection = ({ phoneNumber, onVoiceSelected, currentVoiceSettings }) => {
  const [voices, setVoices] = useState({ male: [], female: [], neutral: [] });
  const [selectedGender, setSelectedGender] = useState(() => {
    return localStorage.getItem('selectedGender') || 'female';
  });
  const [selectedVoice, setSelectedVoice] = useState(() => {
    const saved = localStorage.getItem('selectedVoice');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(true);
  const [streamingEnabled, setStreamingEnabled] = useState(false);
  const [error, setError] = useState(null);
  const [personalityTraits, setPersonalityTraits] = useState({
    formality: 'Strictly Professional',
    verbosity: 'Concise',
    empathy: 'Polite and Neutral',
    proactivity: 'Offers Clarifications',
    humor: 'Completely Serious'
  });
  const { user } = useAuth();

  useEffect(() => {
    const initializeComponent = async () => {
      await loadAvailableVoices();
      await loadExistingSettings();
      if (user) {
        initializeStreaming();
      }
    };
    initializeComponent();
    
    // Don't disconnect on unmount - keep WebSocket persistent
    // return () => {
    //   voiceStreamingService.disconnect();
    // };
  }, [user]);
  
  const loadExistingSettings = async () => {
    try {
      const cognitoUserId = user?.attributes?.sub;
      if (!cognitoUserId) return;
      
      const config = await configService.getBusinessConfigByUserId(cognitoUserId);
      let voiceFound = false;
      
      if (config) {
        // Load existing voice settings
        if (config.voice_settings && Object.keys(voices).length > 0) {
          // Find which gender category contains this voice_id
          const voiceId = config.voice_settings.voice_id;
          for (const [gender, voiceList] of Object.entries(voices)) {
            const matchingVoice = voiceList.find(voice => voice.voice_id === voiceId);
            if (matchingVoice) {
              setSelectedGender(gender);
              setSelectedVoice(matchingVoice);
              // Cache in localStorage for next page load
              localStorage.setItem('selectedGender', gender);
              localStorage.setItem('selectedVoice', JSON.stringify(matchingVoice));
              voiceFound = true;
              break;
            }
          }
        }
        
        // Load existing personality traits
        if (config.formality || config.verbosity || config.empathy || config.proactivity || config.humor) {
          setPersonalityTraits({
            formality: config.formality || 'Business Casual',
            verbosity: config.verbosity || 'Balanced',
            empathy: config.empathy || 'Warm and Understanding',
            proactivity: config.proactivity || 'Suggestive',
            humor: config.humor || 'Completely Serious'
          });
        }
      }
      
      // Set defaults if no existing voice configuration found
      if (!voiceFound && Object.keys(voices).length > 0) {
        const defaultGender = 'female';
        if (voices[defaultGender]?.length > 0) {
          const defaultVoice = voices[defaultGender][0];
          setSelectedGender(defaultGender);
          setSelectedVoice(defaultVoice);
          localStorage.setItem('selectedGender', defaultGender);
          localStorage.setItem('selectedVoice', JSON.stringify(defaultVoice));
        }
      }
    } catch (error) {
      console.warn('Could not load existing voice settings:', error);
      // Set defaults on error as well
      if (Object.keys(voices).length > 0) {
        const defaultGender = 'female';
        if (voices[defaultGender]?.length > 0) {
          const defaultVoice = voices[defaultGender][0];
          setSelectedGender(defaultGender);
          setSelectedVoice(defaultVoice);
          localStorage.setItem('selectedGender', defaultGender);
          localStorage.setItem('selectedVoice', JSON.stringify(defaultVoice));
        }
      }
    }
  };

  const getAccessToken = () => {
    // Find Cognito access token in localStorage
    for (const key of Object.keys(localStorage)) {
      if (key.includes('accessToken') && key.includes('CognitoIdentityServiceProvider')) {
        const token = localStorage.getItem(key);
        if (token && token.split('.').length === 3) {
          return token;
        }
      }
    }
    return null;
  };

  const initializeStreaming = async () => {
    try {
      if (!user) {
        setStreamingEnabled(false);
        return;
      }
      
      const token = getAccessToken();
      
      if (token) {
        voiceStreamingService.setAuthToken(token);
        await voiceStreamingService.connect(token);
        setStreamingEnabled(true);
      } else {
        setStreamingEnabled(false);
      }
    } catch (error) {
      console.log('WebSocket streaming not available, using fallback:', error);
      setStreamingEnabled(false);
    }
  };

  useEffect(() => {
    // Re-run existing settings load when voices are available
    if (Object.keys(voices).length > 0 && user) {
      loadExistingSettings();
    }
  }, [voices, user]);

  const loadAvailableVoices = async () => {
    try {
      setLoading(true);
      console.log('🎤 DEBUG: Starting loadAvailableVoices...');
      
      const data = await configService.getAvailableVoices();
      console.log('🎤 DEBUG: getAvailableVoices response:', data);
      
      if (data.success) {
        console.log('🎤 DEBUG: Voices loaded successfully:', data.voices);
        setVoices(data.voices);
        
        // Don't auto-select - let loadExistingSettings handle selection
      } else {
        console.log('🎤 DEBUG: Voice loading failed:', data.error);
        setError(data.error || 'Failed to load voices');
      }
    } catch (err) {
      console.log('🎤 DEBUG: Voice loading exception:', err);
      setError(err.message || 'Failed to load available voices');
      console.error('Voice loading error:', err);
    } finally {
      setLoading(false);
      console.log('🎤 DEBUG: loadAvailableVoices completed');
    }
  };

  const handleGenderChange = async (gender) => {
    setSelectedGender(gender);
    // Auto-select first voice in new gender category
    if (voices[gender]?.length > 0) {
      const firstVoice = voices[gender][0];
      setSelectedVoice(firstVoice);
      // Auto-save the first voice selection
      try {
        await configService.saveVoiceSettings(firstVoice);
      } catch (error) {
        console.warn('Auto-save voice failed:', error);
      }
    }
  };

  const handleVoiceChange = async (voice, skipSave = false) => {
    setSelectedVoice(voice);
    // Auto-save voice selection (skip if loading existing config)
    if (!skipSave) {
      try {
        await configService.saveVoiceSettings(voice);
      } catch (error) {
        console.warn('Auto-save voice failed:', error);
      }
    }
  };



  const saveVoiceSelection = async () => {
    console.log('🔧 DEBUG: saveVoiceSelection called');
    console.log('🔧 DEBUG: selectedVoice:', selectedVoice);
    console.log('🔧 DEBUG: personalityTraits:', personalityTraits);
    console.log('🔧 DEBUG: user:', user);
    
    if (!selectedVoice) {
      console.log('❌ DEBUG: No selected voice, returning early');
      setError('Please select a voice first');
      return;
    }

    try {
      console.log('🚀 DEBUG: Starting voice settings save...');
      // Save voice settings using business_id-based API
      const voiceData = await configService.saveVoiceSettings(selectedVoice);
      console.log('✅ DEBUG: Voice settings response:', voiceData);
      
      if (!voiceData.message && !voiceData.success) {
        console.log('❌ DEBUG: Voice settings save failed:', voiceData);
        setError(voiceData.error || 'Failed to save voice settings');
        return;
      }

      console.log('🚀 DEBUG: Starting personality traits save...');
      // Save personality traits using business_id-based API
      const personalityData = await configService.updatePersonalityTraits(
        personalityTraits
      );
      console.log('✅ DEBUG: Personality traits response:', personalityData);
      
      if (personalityData.success || personalityData.message) {
        console.log('🎉 DEBUG: Both saves successful, calling onVoiceSelected');
        onVoiceSelected?.(selectedVoice);
      } else {
        console.log('❌ DEBUG: Personality traits save failed:', personalityData);
        setError('Voice saved but personality traits failed to save');
      }
    } catch (err) {
      console.log('💥 DEBUG: Exception in saveVoiceSelection:', err);
      setError(err.message || 'Failed to save settings');
      console.error('Save error:', err);
    }
  };

  if (loading) {
    return (
      <div className="voice-selection-loading">
        <div className="spinner"></div>
        <p>Loading available voices...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="voice-selection-error">
        <p className="error-message" style={{color: 'red'}}>{error}</p>
        <button onClick={() => { setError(null); loadAvailableVoices(); }} className="retry-button">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="voice-selection">
      {/* Gender Selection */}
      <div className="gender-selection">
        <label>Voice Gender:</label>
        <select
          value={selectedGender}
          onChange={(e) => handleGenderChange(e.target.value)}
          className="voice-dropdown"
        >
          {Object.keys(voices).map(gender => (
            <option key={gender} value={gender} disabled={voices[gender].length === 0}>
              {gender.charAt(0).toUpperCase() + gender.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Voice Selection */}
      {voices[selectedGender]?.length > 0 && (
        <div className="voice-selection-dropdown">
          <label>Voice:</label>
          <select
            value={selectedVoice?.voice_id || ''}
            onChange={(e) => {
              const voice = voices[selectedGender].find(v => v.voice_id === e.target.value);
              handleVoiceChange(voice);
            }}
            className="voice-dropdown"
          >
            {voices[selectedGender].map(voice => (
              <option key={voice.voice_id} value={voice.voice_id}>
                {voice.display_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Personality Presets & Custom Toggle */}
      {selectedVoice && (
        <PersonalityTraits 
          initialTraits={personalityTraits}
          onTraitsChange={setPersonalityTraits}
          selectedVoice={selectedVoice}
          businessConfig={{
            business_name: user?.attributes?.['custom:business_name'] || 'Your Business',
            phone_number: phoneNumber
          }}
        />
      )}




    </div>
  );
};

export default VoiceSelection;